import DropzoneController from "./dropzone_controller";

export default class extends DropzoneController {
  static targets = [...DropzoneController.targets];
  static values = {
    taskId: String,
    isTaxTask: { type: Boolean, default: false },
    ...DropzoneController.values
  };

  get previewTemplate() {
    return `
      <li class="dz-preview dz-file-preview list-group-item px-3 py-2 attachment-list-item">
        <div class="d-flex align-items-center">
          <!-- Clickable Rounded Square Image/Icon -->
          <a href="#" class="text-decoration-none dz-preview-link">
            <div class="attachment-preview rounded border d-flex align-items-center justify-content-center flex-shrink-0 me-3" style="width: 48px; height: 48px; overflow: hidden;">
              <!-- Image preview -->
              <img data-dz-thumbnail class="w-100 h-100 d-none" style="object-fit: cover;"/>

              <!-- File icon for non-images -->
              <div class="dz-file-content d-flex align-items-center justify-content-center w-100 h-100">
                <i class="dz-file-icon fas fa-2x text-muted"></i>
              </div>
            </div>
          </a>

          <!-- Name and Size -->
          <div class="flex-grow-1 min-w-0">
            <div class="text-truncate fw-medium mb-0">
              <span data-dz-name></span>
            </div>
            <small class="text-muted d-block" data-dz-size></small>
          </div>

          <!-- Action Icons -->
          <div class="ms-auto d-flex align-items-center gap-2">
            <a href="#" class="btn btn-sm btn-link text-muted p-1 attachment-action-btn dz-download-btn" title="Download" style="opacity: 0; transition: opacity 0.2s;" target="_blank">
              <i class="fas fa-download"></i>
            </a>
            <a href="#" class="dz-remove btn btn-sm btn-link text-muted p-1 attachment-action-btn" data-dz-remove title="Delete" style="opacity: 0; transition: opacity 0.2s;">
              <i class="fas fa-trash"></i>
            </a>
          </div>
        </div>

        <!-- Progress bar -->
        <div class="dz-progress" style="display: none;">
          <div class="progress mt-2" style="height: 4px;">
            <div class="dz-upload progress-bar bg-primary" data-dz-uploadprogress style="width: 0%;"></div>
          </div>
        </div>

        <!-- Success mark -->
        <div class="dz-success-mark" style="display: none;">
          <i class="fa fa-check-circle text-success"></i>
        </div>

        <!-- Error message -->
        <div class="dz-error-message" style="display: none;">
          <span class="text-danger small mt-1" data-dz-errormessage></span>
        </div>
      </li>
    `;
  }

  connect() {
    this.resolveTaskId();
    super.connect();
  }

  resolveTaskId() {
    this.actualTaskId = null;

    // Try multiple approaches to get task ID
    if (this.taskIdValue && typeof this.taskIdValue === 'string' && this.taskIdValue.trim()) {
      this.actualTaskId = this.taskIdValue;
    } else if (this.element.dataset.taskDropzoneTaskIdValue) {
      this.actualTaskId = this.element.dataset.taskDropzoneTaskIdValue;
    } else {
      const urlMatch = window.location.pathname.match(/\/tasks\/([a-f0-9-]+)/);
      if (urlMatch) this.actualTaskId = urlMatch[1];
    }

    if (!this.actualTaskId) {
      console.error("Could not determine task ID for dropzone controller");
    }
  }

  // Override the Dropzone.js initialization to handle missing targets
  initializeDropzone() {
    super.initializeDropzone();
    
    // Add task-specific data using the sending event
    this.dropzone.on("sending", (file, xhr, formData) => {
      console.log('task dropzone sending event fired');
      formData.append("attachment[attachable_type]", "Task");
      if (this.actualTaskId) {
        formData.append("attachment[attachable_id]", this.actualTaskId);
      }
    });
  }

  // Override fileRemoved to prevent hiding preview when existing attachments are present
  fileRemoved(file) {
    // Remove from uploaded attachments if it was successfully uploaded
    if (file.attachmentId) {
      this.removeUploadedAttachment(file.attachmentId);
    }

    // Don't hide preview - we have server-rendered attachments that should stay visible
    // Note: Count updates are handled by server responses or Turbo Streams on delete
  }

  // Override fileUploaded to add task-specific behavior
  fileUploaded(file, response) {
    super.fileUploaded(file, response);

    if (response.success) {
      // Replace with clickable attachment after delay
      setTimeout(() => this.makeAttachmentClickable(file, response), 1000);

      // Update count from server response
      if (response.attachments_count !== undefined) {
        const countElement = document.getElementById("attachments-count");
        if (countElement) {
          countElement.textContent = `(${response.attachments_count})`;
        }
      }
    }
  }

  makeAttachmentClickable(file, result) {
    const taskId = this.actualTaskId;
    if (!taskId || !file.previewElement) return;

    // Update classes to match existing attachments (list view)
    file.previewElement.className = "list-group-item px-3 py-2 attachment-list-item";
    file.previewElement.id = `attachment-${result.id}`; // Set ID so Turbo Stream can find it

    // Remove the remove button
    const removeBtn = file.previewElement.querySelector(".dz-remove");
    if (removeBtn) {
      removeBtn.remove();
    }

    // Hide success mark and progress bar
    const successMark = file.previewElement.querySelector(".dz-success-mark");
    const progressBar = file.previewElement.querySelector(".dz-progress");
    if (successMark) successMark.style.display = "none";
    if (progressBar) progressBar.style.display = "none";

    // Replace low-res thumbnail with high-res image if it's an image file
    const thumbnail = file.previewElement.querySelector("[data-dz-thumbnail]");
    const fileContent = file.previewElement.querySelector(".dz-file-content");

    if (thumbnail && result.url && this.isImageFile(file.name)) {
      thumbnail.src = result.url;
      thumbnail.classList.remove("d-none");
      // Hide file icon content for images
      if (fileContent) fileContent.classList.add("d-none");
    }

    // Make the preview square clickable (update existing link)
    const previewLink = file.previewElement.querySelector(".dz-preview-link");
    if (previewLink) {
      previewLink.href = `/tasks/${taskId}/attachments/${result.id}`;
      previewLink.dataset.turboFrame = "updated-modal-content";
    }

    // Make the name clickable
    const nameContainer = file.previewElement.querySelector(".flex-grow-1.min-w-0");
    if (nameContainer) {
      const nameDiv = nameContainer.querySelector(".text-truncate");
      if (nameDiv) {
        const nameLink = document.createElement("a");
        nameLink.href = `/tasks/${taskId}/attachments/${result.id}`;
        nameLink.className = "text-decoration-none";
        nameLink.dataset.turboFrame = "updated-modal-content";

        // Move the div content into the link
        nameLink.innerHTML = nameDiv.innerHTML;
        nameDiv.innerHTML = "";
        nameDiv.appendChild(nameLink);
      }
    }

    // Add tax return badge frame for tax tasks
    if (this.isTaxTaskValue && result.id) {
      const badgeFrame = document.createElement("turbo-frame");
      badgeFrame.id = `tax_return_badge_${result.id}`;

      const form = document.createElement("form");
      form.method = "post";
      form.action = `/tasks/tax-tasks/${taskId}/tax_return_attachment`;
      form.dataset.turboStream = "true";
      form.innerHTML = `
        <input type="hidden" name="_method" value="patch">
        <input type="hidden" name="authenticity_token" value="${document.querySelector('meta[name="csrf-token"]')?.content || ''}">
        <input type="hidden" name="attachment_id" value="${result.id}">
        <button type="submit" class="btn btn-link btn-sm text-muted p-0 attachment-action-btn" style="font-size: 0.75rem; opacity: 0; transition: opacity 0.2s;">Set as tax return</button>
      `;
      badgeFrame.appendChild(form);

      const actionsDiv = file.previewElement.querySelector(".ms-auto.d-flex");
      if (actionsDiv) {
        actionsDiv.parentNode.insertBefore(badgeFrame, actionsDiv);
      }
    }

    // Find the action icons container
    const actionsContainer = file.previewElement.querySelector(".ms-auto.d-flex");
    if (actionsContainer) {
      // Clear existing content
      actionsContainer.innerHTML = "";

      // Add download button (with hover effect)
      const downloadBtn = document.createElement("a");
      downloadBtn.href = result.download_url_with_filename || result.download_url || result.url || `/tasks/${taskId}/attachments/${result.id}`;
      downloadBtn.className = "btn btn-sm btn-link text-muted p-1 attachment-action-btn";
      downloadBtn.style.cssText = "opacity: 0; transition: opacity 0.2s;";
      downloadBtn.title = "Download";
      downloadBtn.target = "_blank";
      downloadBtn.download = file.name;
      downloadBtn.innerHTML = '<i class="fas fa-download"></i>';
      actionsContainer.appendChild(downloadBtn);

      // Add delete button (with hover effect)
      const deleteBtn = document.createElement("a");
      deleteBtn.href = `/attachments/${result.id}`;
      deleteBtn.className = "btn btn-sm btn-link text-muted p-1 attachment-action-btn";
      deleteBtn.style.cssText = "opacity: 0; transition: opacity 0.2s;";
      deleteBtn.title = "Delete";
      deleteBtn.setAttribute("data-turbo-method", "delete");
      deleteBtn.setAttribute("data-turbo-confirm", "Are you sure you want to delete this attachment?");
      deleteBtn.setAttribute("rel", "nofollow");
      deleteBtn.setAttribute("data-method", "delete");
      deleteBtn.innerHTML = '<i class="fas fa-trash"></i>';
      actionsContainer.appendChild(deleteBtn);
    }
  }

  // Helper method to check if file is an image
  isImageFile(filename) {
    const extension = filename.split('.').pop().toLowerCase();
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "tiff", "tif"];
    return imageExtensions.includes(extension);
  }

  updateAttachmentCount() {
    const countElement = document.getElementById("attachments-count");
    if (countElement) {
      // Count all attachments by looking for elements with id="attachment-{id}"
      // This includes both server-rendered attachments and uploaded attachments
      // that have been converted via makeAttachmentClickable()
      const totalCount = this.previewTarget.querySelectorAll('[id^="attachment-"]').length;
      countElement.textContent = `(${totalCount})`;
    }
  }
}
